import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { ErrorBanner } from '@/components/app/error-banner'
import { LogoMark } from '@/components/app/logo-mark'
import { SubmitButton } from '@/components/app/submit-button'
import { apiPost, setAccessToken } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { getAuthUser } from '@/lib/auth'
import { clearAuthFlow, getAuthFlow } from '@/lib/auth-flow'
import { getHomeRoute } from '@/lib/roles'

export const Route = createFileRoute('/connexion/pin')({
  beforeLoad: () => {
    if (!getAuthFlow()?.phone) throw redirect({ to: '/connexion' })
  },
  component: PinScreen,
})

interface TokenResponse {
  accessToken: string
  refreshToken: string
}

const PIN_LENGTH = 4

function PinScreen() {
  const navigate = useNavigate()
  const flow = getAuthFlow()
  const isSetup = flow?.needsPin === true

  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const canSubmit = pin.length === PIN_LENGTH && (!isSetup || confirm.length === PIN_LENGTH)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit || loading) return

    if (isSetup && pin !== confirm) {
      setError('Les codes ne correspondent pas.')
      setConfirm('')
      return
    }

    setError(null)
    setLoading(true)

    try {
      // Le refresh token est aussi posé en cookie HttpOnly par le serveur ;
      // seul l'access token est conservé en mémoire côté client.
      const res = isSetup
        ? await apiPost<TokenResponse>('auth/mobile/set-pin', {
            otpToken: flow?.otpToken,
            pin,
          })
        : await apiPost<TokenResponse>('auth/mobile/login-pin', {
            phone: flow?.phone,
            pin,
          })

      setAccessToken(res.accessToken)
      clearAuthFlow()

      const user = getAuthUser()
      await navigate({ to: getHomeRoute(user?.roles ?? []) })
    } catch (err) {
      setError(getApiErrorMessage(err, isSetup ? 'Impossible de créer le PIN.' : 'PIN incorrect.'))
      setPin('')
      setConfirm('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-navy-950 px-6 pt-safe pb-safe">
      <div className="mx-auto w-full max-w-sm">
        <LogoMark caption={isSetup ? 'CRÉER VOTRE PIN' : 'CONNEXION'} />

        <form onSubmit={handleSubmit} className="mt-10 flex flex-col gap-5">
          <div>
            <h2 className="text-[20px] font-semibold text-chalk">
              {isSetup ? 'Choisissez un code à 4 chiffres' : 'Entrez votre code PIN'}
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-steel">
              {isSetup
                ? 'Il vous servira à vous connecter les prochaines fois.'
                : 'Le code que vous avez défini lors de votre première connexion.'}
            </p>
          </div>

          <PinField
            label={isSetup ? 'Nouveau PIN' : 'Code PIN'}
            value={pin}
            onChange={setPin}
            autoFocus
            autoComplete={isSetup ? 'new-password' : 'current-password'}
          />

          {isSetup && (
            <PinField
              label="Confirmer le PIN"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          )}

          <ErrorBanner message={error} />

          <SubmitButton disabled={!canSubmit} loading={loading}>
            {isSetup ? 'Créer mon PIN' : 'Se connecter'}
          </SubmitButton>
        </form>
      </div>
    </div>
  )
}

interface PinFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  autoFocus?: boolean
  autoComplete?: string
}

function PinField({ label, value, onChange, autoFocus, autoComplete }: PinFieldProps) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[11px] font-semibold tracking-[0.8px] text-steel uppercase">
        {label}
      </span>
      <input
        type="password"
        inputMode="numeric"
        // Le clavier numérique iOS n'apparaît qu'avec ce pattern.
        pattern="[0-9]*"
        maxLength={PIN_LENGTH}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
        // biome-ignore lint/a11y/noAutofocus: écran mono-champ dédié à la saisie du PIN — le focus automatique évite une frappe de plus sur mobile, comme sur l'app native
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        placeholder="••••"
        className="h-14 rounded-[14px] border border-navy-800 bg-navy-900 px-4 text-center text-[22px] tracking-[8px] text-chalk placeholder:text-mist focus:border-gold focus:outline-none"
      />
    </label>
  )
}
